import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { ScrollArea } from '../../components/ui/scroll-area';
import { 
  Search, Eye, History, User, Building2, FileText, 
  Plus, Pencil, Trash2, RefreshCw, Euro, CheckCircle2, RotateCcw, FileCheck, FileX, Send
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const AuditLogsPage = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    entity_type: 'all',
    action: 'all',
    limit: 100
  });
  const [selectedLog, setSelectedLog] = useState(null);
  const [showDetailDialog, setShowDetailDialog] = useState(false);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.entity_type && filters.entity_type !== 'all') params.append('entity_type', filters.entity_type);
      if (filters.action && filters.action !== 'all') params.append('action', filters.action);
      params.append('limit', filters.limit.toString());

      const response = await axios.get(`${API_URL}/audit-trail?${params.toString()}`);
      setLogs(response.data);
    } catch (error) {
      toast.error('Errore nel caricamento dei log');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const getActionIcon = (action) => {
    switch (action) {
      case 'create': return <Plus className="w-4 h-4" />;
      case 'update': return <Pencil className="w-4 h-4" />;
      case 'delete': return <Trash2 className="w-4 h-4" />;
      case 'payment_completed': return <CheckCircle2 className="w-4 h-4" />;
      case 'payment_reopened': return <RotateCcw className="w-4 h-4" />;
      case 'payment_invoice_created': return <FileCheck className="w-4 h-4" />;
      case 'payment_invoice_deleted': return <FileX className="w-4 h-4" />;
      case 'payment_distinta_generated': return <Send className="w-4 h-4" />;
      default: return <History className="w-4 h-4" />;
    }
  };

  const getActionBadge = (action) => {
    const styles = {
      create: 'bg-green-100 text-green-700',
      update: 'bg-blue-100 text-blue-700',
      delete: 'bg-red-100 text-red-700',
      password_reset: 'bg-amber-100 text-amber-700',
      payment_completed: 'bg-emerald-100 text-emerald-700',
      payment_reopened: 'bg-orange-100 text-orange-700',
      payment_invoice_created: 'bg-teal-100 text-teal-700',
      payment_invoice_deleted: 'bg-red-100 text-red-700',
      payment_distinta_generated: 'bg-indigo-100 text-indigo-700'
    };
    const labels = {
      create: 'Creazione',
      update: 'Modifica',
      delete: 'Eliminazione',
      password_reset: 'Reset Password',
      payment_completed: 'Pagamento concluso',
      payment_reopened: 'Pagamento riaperto',
      payment_invoice_created: 'Fattura emessa',
      payment_invoice_deleted: 'Fattura cancellata',
      payment_status_change: 'Cambio stato pagamento',
      payment_distinta_generated: 'Distinta generata'
    };
    return (
      <Badge className={`${styles[action] || 'bg-slate-100 text-slate-700'} flex items-center gap-1`}>
        {getActionIcon(action)}
        {labels[action] || action}
      </Badge>
    );
  };

  const getEntityIcon = (type) => {
    switch (type) {
      case 'user': return <User className="w-4 h-4" />;
      case 'property': return <Building2 className="w-4 h-4" />;
      case 'contract': return <FileText className="w-4 h-4" />;
      case 'payment': return <Euro className="w-4 h-4" />;
      default: return <History className="w-4 h-4" />;
    }
  };

  const getEntityLabel = (type) => {
    const labels = {
      user: 'Utente',
      property: 'Immobile',
      contract: 'Contratto',
      payment: 'Pagamento'
    };
    return labels[type] || type;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const viewDetails = (log) => {
    setSelectedLog(log);
    setShowDetailDialog(true);
  };

  const renderChanges = (changes) => {
    if (!changes || typeof changes !== 'object') return null;
    
    return (
      <div className="space-y-2">
        {Object.entries(changes).map(([key, value]) => (
          <div key={key} className="flex items-start gap-2 p-2 bg-slate-50 rounded text-sm">
            <span className="font-medium text-slate-700 min-w-32">{key}:</span>
            <span className="text-slate-600 break-all">
              {typeof value === 'object' ? JSON.stringify(value, null, 2) : String(value)}
            </span>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div data-testid="audit-logs-page">
      <div className="page-header">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Registro Attività</h1>
            <p className="text-slate-500 mt-1">Traccia tutte le operazioni effettuate nel sistema</p>
          </div>
          <Button onClick={fetchLogs} variant="outline" data-testid="refresh-logs-btn">
            <RefreshCw className="w-4 h-4 mr-2" />
            Aggiorna
          </Button>
        </div>
      </div>

      <div className="page-content">
        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="p-4">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="w-48">
                <Select 
                  value={filters.entity_type} 
                  onValueChange={(v) => setFilters({...filters, entity_type: v})}
                >
                  <SelectTrigger data-testid="filter-entity-type">
                    <SelectValue placeholder="Tipo Entità" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tutte</SelectItem>
                    <SelectItem value="user">Utenti</SelectItem>
                    <SelectItem value="property">Immobili</SelectItem>
                    <SelectItem value="contract">Contratti</SelectItem>
                    <SelectItem value="payment">Pagamenti</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="w-48">
                <Select 
                  value={filters.action} 
                  onValueChange={(v) => setFilters({...filters, action: v})}
                >
                  <SelectTrigger data-testid="filter-action">
                    <SelectValue placeholder="Tipo Azione" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tutte</SelectItem>
                    <SelectItem value="create">Creazione</SelectItem>
                    <SelectItem value="update">Modifica</SelectItem>
                    <SelectItem value="delete">Eliminazione</SelectItem>
                    <SelectItem value="password_reset">Reset Password</SelectItem>
                    <SelectItem value="payment_completed">Pagamento concluso</SelectItem>
                    <SelectItem value="payment_reopened">Pagamento riaperto</SelectItem>
                    <SelectItem value="payment_invoice_created">Fattura emessa</SelectItem>
                    <SelectItem value="payment_invoice_deleted">Fattura cancellata</SelectItem>
                    <SelectItem value="payment_distinta_generated">Distinta generata</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="w-32">
                <Select 
                  value={filters.limit.toString()} 
                  onValueChange={(v) => setFilters({...filters, limit: parseInt(v)})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Limite" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="50">50</SelectItem>
                    <SelectItem value="100">100</SelectItem>
                    <SelectItem value="200">200</SelectItem>
                    <SelectItem value="500">500</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Logs Table */}
        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="loading-spinner" />
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Data/Ora</TableHead>
                    <TableHead>Utente</TableHead>
                    <TableHead>Entità</TableHead>
                    <TableHead>Azione</TableHead>
                    <TableHead>ID Entità</TableHead>
                    <TableHead className="text-right">Dettagli</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {logs.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8 text-slate-500">
                        Nessun log trovato
                      </TableCell>
                    </TableRow>
                  ) : (
                    logs.map((log, index) => (
                      <TableRow key={log.id || index} data-testid={`audit-row-${index}`}>
                        <TableCell className="text-sm text-slate-600">
                          {formatDate(log.timestamp)}
                        </TableCell>
                        <TableCell>
                          <span className="text-sm">{log.user_email || '-'}</span>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            {getEntityIcon(log.entity_type)}
                            <span>{getEntityLabel(log.entity_type)}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          {getActionBadge(log.action)}
                        </TableCell>
                        <TableCell>
                          <span className="text-xs font-mono text-slate-500">
                            {log.entity_id?.substring(0, 8)}...
                          </span>
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => viewDetails(log)}
                            data-testid={`view-log-${index}`}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Detail Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="w-5 h-5" />
              Dettaglio Operazione
            </DialogTitle>
          </DialogHeader>
          {selectedLog && (
            <ScrollArea className="max-h-[60vh]">
              <div className="space-y-4">
                {/* Info base */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <span className="text-xs text-slate-500">Data/Ora</span>
                    <p className="font-medium">{formatDate(selectedLog.timestamp)}</p>
                  </div>
                  <div>
                    <span className="text-xs text-slate-500">Eseguito da</span>
                    <p className="font-medium">{selectedLog.user_email || '-'}</p>
                  </div>
                  <div>
                    <span className="text-xs text-slate-500">Entità</span>
                    <p className="font-medium flex items-center gap-1">
                      {getEntityIcon(selectedLog.entity_type)}
                      {getEntityLabel(selectedLog.entity_type)}
                    </p>
                  </div>
                  <div>
                    <span className="text-xs text-slate-500">Azione</span>
                    <div className="mt-1">{getActionBadge(selectedLog.action)}</div>
                  </div>
                </div>

                <div>
                  <span className="text-xs text-slate-500">ID Entità</span>
                  <p className="font-mono text-sm">{selectedLog.entity_id}</p>
                </div>

                {/* Changes */}
                {selectedLog.action === 'update' && selectedLog.old_data && selectedLog.new_data && (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <h4 className="text-sm font-semibold text-slate-700 mb-2">Prima</h4>
                      {renderChanges(selectedLog.old_data)}
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-slate-700 mb-2">Dopo</h4>
                      {renderChanges(selectedLog.new_data)}
                    </div>
                  </div>
                )}

                {selectedLog.action === 'create' && selectedLog.new_data && (
                  <div>
                    <h4 className="text-sm font-semibold text-slate-700 mb-2">Dati Creati</h4>
                    {renderChanges(selectedLog.new_data)}
                  </div>
                )}

                {selectedLog.action === 'delete' && selectedLog.old_data && (
                  <div>
                    <h4 className="text-sm font-semibold text-slate-700 mb-2">Dati Eliminati</h4>
                    {renderChanges(selectedLog.old_data)}
                  </div>
                )}

                {selectedLog.changes && (
                  <div>
                    <h4 className="text-sm font-semibold text-slate-700 mb-2">Dettagli</h4>
                    {renderChanges(selectedLog.changes)}
                  </div>
                )}
              </div>
            </ScrollArea>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AuditLogsPage;
